import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { Button, Spin, PaperIcon } from '@nackle/paper';
import { getString } from '@nackle/intl-tools'; 
import './styles.less';
import { Skeleton, Tooltip } from 'antd';
import { ExportOutlined, LockOutlined } from '@ant-design/icons';
import { createEvent } from 'ics';
import useInterval from '../../hooks/useInterval';

const UNREGISTRABLE_REASONS = {
    FULL: 'ilt.sessions.tip.seatsFilled',
    PAST_REG: 'ilt.sessions.tip.afterRegister',
    BEFORE_REG: 'ilt.sessions.tip.beforeRegister',
    REGISTERED: 'ilt.sessions.tip.alreadyRegistered',
    STARTED: 'ilt.sessions.tip.started',
    FINISHED: 'ilt.sessions.tip.finished',
    CANCELLED: 'ilt.sessions.tip.cancelled',
    LOCKED: 'locked.message.course',
};

const SessionItemView = ( { item, registrationInfo, updateRegistrationCb, isLocked,
    loading, options: _options, children } ) => {


    const [ registrability, setRegistrability ] = useState( null );

    const [ regLoading, setRegLoading ] = useState( true );

    function checkRegistrable() {

        const now = moment();

        if ( isLocked ) {
            const reg = {
                isRegistrable: false,
                lastChecked: now,
            };
            setRegistrability( reg );
            setRegLoading( false );
            return reg;
        }

        if ( !registrationInfo ) {
            return {
                lastChecked: now,
                isRegistrable: false
            };
        }

        let reason = null;
        let reasonVar;

        const isFull = item.maxSeats <= item.filledSeats;

        if ( isFull ) {
            reason = UNREGISTRABLE_REASONS.FULL;
        }

        const isPastRegistration = item.registrationEnd && now.isAfter( item.registrationEnd );
        
        if ( isPastRegistration ) {
            reason = UNREGISTRABLE_REASONS.PAST_REG;
            reasonVar = [ 'date', item.registrationEnd.tz( moment.tz.guess() ).format( 'LLL z' ) ];
        }
        
        const isStarted = now.isAfter( item.sessionStart );

        if ( isStarted ) {
            reason = UNREGISTRABLE_REASONS.STARTED;
        }

        const isFinished = now.isAfter( item.sessionEnd );

        if ( isFinished ) {
            reason = UNREGISTRABLE_REASONS.FINISHED;
        }
        
        const isBeforeRegistration = item.registrationStart && now.isBefore( item.registrationStart );

        if ( isBeforeRegistration ) {
            reason = UNREGISTRABLE_REASONS.BEFORE_REG;
            reasonVar = [ 'date', item.registrationStart.tz( moment.tz.guess() ).format( 'LLL z' ) ];
        }
        
        const isRegisteredHere = registrationInfo.isRegistered && registrationInfo.sessionId == item.id;
        const isRegisteredElsewhere = registrationInfo.isRegistered && !isRegisteredHere;

        if ( isRegisteredElsewhere ) {
            reason = UNREGISTRABLE_REASONS.REGISTERED;
        }
        
        if ( isRegisteredHere || registrationInfo.isCompleted ) {
            reason = null;
        }

        const isCancelled = !!item.isCancelled;

        if ( isCancelled ) {
            reason = UNREGISTRABLE_REASONS.CANCELLED;
        }
        
        const isRegistrable = 
            !registrationInfo.isCompleted &&
            !isPastRegistration && !isBeforeRegistration &&
            !isStarted &&
            !isFull && 
            !isCancelled &&
            isRegisteredHere == false && isRegisteredElsewhere == false;

        if ( reason ) {
            reason = getString( reason );
            if ( reasonVar ) {
                reason = reason.replace( `{{${ reasonVar[ 0 ] }}}`, reasonVar[ 1 ] );
            }
        }

        const reg = {
            reason: reason,
            lastChecked: now,
            isRegisteredHere,
            isBeforeRegistration,
            isCancelled,
            isRegistrable
        };

        setRegistrability( reg );
        setRegLoading( false );

        return reg;
    }

    useInterval( checkRegistrable, 1000 * 60 ); // 1 minute

    useMemo( () => {
        checkRegistrable();
    }, [ item, registrationInfo ] );

    async function handleActionClick( register ) {
        
        const reg = checkRegistrable();

        if ( reg.isRegistrable && register ) {
            await updateRegistrationCb( {}, true ); // trigger load for all items
            await registrationInfo.register( item.id );
        } else if ( reg.isRegisteredHere && !register ) {
            await updateRegistrationCb( {}, true );
            await registrationInfo.unregister( item.id, registrationInfo.registrationId );
        }
        
    }

    const statusIndicator = useMemo( () => { 
        if ( isLocked ) {
            return <LockOutlined />;
        } else if ( registrationInfo?.isCompleted && registrability?.isRegisteredHere ) {
            return <PaperIcon icon='CheckCircle' collection='antd-outlined' className='primary success' />;
        } else if ( registrability?.isRegisteredHere ) {
            return <PaperIcon icon='Hourglass' collection='antd-outlined' />;
        }
        return <></>;
    }, [ registrationInfo, registrability ] );

    const statusClass = useMemo( () => {
        if ( isLocked ) {
            return 'status-locked';
        } else if ( registrationInfo?.isCompleted ) {
            return 'status-completed';
        } else if ( registrability?.isCancelled ) {
            return 'status-cancelled';
        } else if ( registrability?.isRegisteredHere ) {
            return 'status-registered';
        } else if ( registrability?.isRegistrable ) {
            return 'status-registrable';
        } else {
            return 'status-nonregistrable';
        }
    }, [ registrability ] );

    const actionButton = useMemo( () => {

        if ( loading || regLoading || !registrability ) {
            return <Skeleton.Button active={ true } />;
        }

        const isDisabled = isLocked
            || ( !registrability.isRegistrable && !registrability.isRegisteredHere ) 
            || registrationInfo.isCompleted 
            || ( registrability.isCancelled && !registrability.isRegisteredHere );

        const [ buttonFunction, label ] = 
            isLocked ? [ 'btn-locked', getString( 'learningObject.actions.locked' ) ] :
            registrationInfo?.isCompleted && registrability.isRegisteredHere ? [ 'btn-completed', getString( 'status.completed' ) ] :
            registrability.isRegisteredHere ? [ 'btn-unregister', getString( 'ilt.sessions.unregister' ) ] :
            registrability.isCancelled ? [ 'btn-cancelled', getString( 'ilt.sessions.alert.cancelled' ) ] :
            [ 'btn-register', getString( 'ilt.sessions.register' ) ];

        const button = <Button 
            className={ 
                classNames( 
                    'mini', 
                    'action-button', 
                    'primary', 
                    statusClass,
                    buttonFunction,
                )
            }
            onClick={ () => handleActionClick( !registrability.isRegisteredHere ) }
            disabled={ isDisabled }
        >
            { label }
        </Button>; 

        if ( isDisabled && registrability.reason ) {
            return <Tooltip title={ registrability.reason }>
                { button }
            </Tooltip>;
        }

        return button;
    }, [ loading, regLoading, registrability, item ] );

    const alertMessage = useMemo( () => {
        if ( registrability?.isCancelled ) {
            return <>{ getString( 'ilt.sessions.alert.cancelled' ) }</>;
        }
        return null;
    }, [ registrability ] );

    const registrationLink = useMemo( () => {
        return item.registrationUrl?.length > 0 && registrability?.isRegisteredHere && !registrationInfo.isCompleted && !item.isCancelled ? 
            <span className='registration-link'>
                <Tooltip placement="bottom" title={ getString( 'ilt.sessions.tip.registrationLink' ) }>
                    <a className='mini' target='_blank' href={ item.registrationUrl } rel="noreferrer">{ getString( 'ilt.session.registrationLink' ) }<ExportOutlined className='tiny-icon'/></a>
                </Tooltip>
            </span> : <></>;
    }, [ item, registrability, registrationInfo ] );

    const formattedIcon = useMemo( () => {
        if ( item.isVirtual ) {
            return <PaperIcon collection='antd-outlined' icon='Desktop' />;
        } else {
            return <PaperIcon collection='antd-outlined' icon='User' />;
        }
    }, [ item ] );

    // this is stupid - moment outputs month as a 0-index but ics imports it as 1-index
    function momentToIcsArrayConversion( dt ) {
        const arr = dt.toArray();
        arr[ 1 ] = arr[ 1 ] + 1;
        return arr;
    }

    async function handleDownload() {
        const filename = `${ item.name }.ics`;
        const event = {
            start: momentToIcsArrayConversion( item.sessionStart ),
            end: momentToIcsArrayConversion( item.sessionEnd ),
            title: `${ item.courseName } — ${ item.name }`,
            description: item.courseDescription,
            location: item.locationString,
            htmlContent: `<!DOCTYPE html><html><body><h3>${ item.courseName }</h3><h4>${ item.name }</h4><p>${ item.courseDescription }</p><a href="${ item.locationLink }">${ item.locationString }</a></body></html>`,
            organizer: item.instructors.map( i => ( { name: i.name, email: i.emailAddress } ) )[ 0 ],
        };
        const file = await new Promise( ( resolve, reject ) => {
            createEvent( event, ( error, value ) => {
                if ( error ) {
                    reject( error );
                }

                resolve( new File( [ value ], filename, { type: 'text/calendar' } ) );
            } );
        } );
        const url = URL.createObjectURL( file );

        // trying to assign the file URL to a window could cause cross-site
        // issues so this is a workaround using HTML5
        const anchor = document.createElement( 'a' );
        anchor.href = url;
        anchor.download = filename;

        document.body.appendChild( anchor );
        anchor.click();
        document.body.removeChild( anchor );

        

        URL.revokeObjectURL( url );
    }

    return (
        <div className={ classNames( 'session-item-view', 'item-view', statusClass ) } >

            { registrability?.isCancelled && <div className='overlay' /> }
            
            <div className='header-section'>
                <div className='icon'>
                    { formattedIcon }
                </div> 
                <div className='title-group'>
                    { loading && !item.name ? 
                        <Skeleton active paragraph={ { rows: 1, width: '50%' } } title={ false } /> :
                        <span className='title _subtitle'>
                            { item.name }
                        </span>
                        }
                    { alertMessage &&
                        <span className='supernote mini text-em text-it text-cap highlight'>
                            { alertMessage }
                        </span>
                    }
                </div>
            </div>

            <div className='action-section'>
                { loading ? <Spin key="spin"/> : statusIndicator ?? <></> }
                { actionButton }
                { registrationLink }
            </div>

            <div className='body-section'>
                <div className='description _body'>
                    <SessionDetail key='datetime' details={ [
                        { title: getString( 'ilt.sessions.date' ), description: item?.sessionStart?.format( 'L' ) },
                        { title: getString( 'ilt.sessions.time' ), description: ( item?.sessionStart && item.sessionEnd ) ? `${ item.sessionStart.format( 'LT' ) } - ${ item.sessionEnd.tz( moment.tz.guess() ).format( 'LT z' ) }` : null },
                        { description: !item ? null : <span className='text-em highlight clickable' onClick={ handleDownload }>{ getString( 'button.download.calendar' ) }<PaperIcon className='tiny-icon' collection='antd-outlined' icon='Download'/></span>, hide: !registrability || !registrability.isRegisteredHere || registrability.isCancelled }
                    ] } />
                    <SessionDetail key='seats-instructor' details={ [
                        { title: getString( 'ilt.sessions.openSeats' ), description: ( item?.maxSeats === undefined || item.filledSeats === undefined ) ? null : getString( 'ilt.sessions.seatsCount' ).replace( '{{available}}', item.maxSeats - item.filledSeats ).replace( '{{total}}', item.maxSeats ) },
                        { title: getString( 'ilt.sessions.instructor' ), description: ( item?.instructors?.length > 0 ) ?
                            <>
                                { item.instructors.map( i => i.emailAddress ? <a key={ `instructor-${ i.id }` } href={ `mailto:${ i.emailAddress }` }>{ i.name }</a> : <p key={ `instructor-${ i.id }` }>{ i.name }</p> ) }
                            </> : null }
                    ] } />
                    <SessionDetail key='title' details={ [
                        { title: getString( 'ilt.sessions.location.title' ), description: ( item?.locationLink && item.locationLabel ) ? <a target='_blank' href={ item.locationLink } rel="noreferrer">{ item.locationLabel }</a> : null }
                    ] } />
                </div>

                { children &&
                    <div className='additional-content'>
                        { children }
                    </div> 
                }
            </div>
        </div>
    );
};

const SessionDetail = ( { details } ) => {
    return <div className='session-detail'>

            { details.map( ( d, i ) => 
                d.hide ? <></> :
                !d.description ? 
                    <Skeleton key={ `loading-${ d.title }` } active paragraph={ { rows: 1 } } title={ false } /> :
                    <div key={ `detail-${ i }` } className='session-detail-item'>
                        { d.title && <p className='text-em body-text detail-title'>{ d.title }</p> }
                        <p className='body-text detail-description'>{ d.description }</p>
                    </div>  ) 
            }
        </div>;
};

export default SessionItemView;
